import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Store, Trash2 } from 'lucide-react';
import { usaMarcaDaLoja, RECUSA_MARCA_FANTASIA } from '@fiodecobre/shared';
import { appApi, ErroApp, type ClienteApp } from './api';
import { estiloCampo } from './campos';

// A MARCA do prestador, dentro de ☰ -> Configuracoes (migration 0237).
//
// Pedido dele em 18/08/2026: *"deve exibir dois novos campos: Imagem e Fantasia... o
// usuario podera subir a foto da logo e inserir o nome da sua empresa. Estas
// informacoes serao exibidas no pdf do orcamento no lugar do nome dele"*.
//
// ⚠️ **Vazio nao e' defeito, e' o padrao** — quem nao configurar segue assinando com
// o proprio nome, que e' como o papel nasceu ([DA-565]). Por isso a tela DIZ o que
// esta valendo agora, em vez de deixar o campo em branco e o prestador adivinhar.
//
// ⚠️ **A logo e' reduzida no APARELHO antes de subir** (600px no lado maior). Uma
// logo de 3000px pra ocupar 2cm de papel engorda o PDF que ele manda no WhatsApp —
// a mesma conta que a foto do chat ja fazia com 1600px.
//
// ⛔ Sem recorte e sem enquadramento obrigatorio: passo que redesenha a imagem do
// usuario ja custou caro nesta casa ([DA-479]). O que sobe e' o que ele escolheu,
// so menor.

const COBRE = '#b45309';
const CINZA = '#6b7280';
const BORDA = '#e7e5e4';

/** Lado maior da logo depois de reduzida. */
const LOGO_LADO_MAX = 600;
const LOGO_QUALIDADE = 0.9;

async function reduzirLogo(arquivo: File): Promise<{ base64: string; mime: string }> {
  const bitmap = await createImageBitmap(arquivo);
  const escala = Math.min(1, LOGO_LADO_MAX / Math.max(bitmap.width, bitmap.height));
  const largura = Math.round(bitmap.width * escala);
  const altura = Math.round(bitmap.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não consegui preparar a imagem.');
  // Fundo branco: logo com transparencia vira preto no JPEG, e o papel e' branco.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, largura, altura);
  ctx.drawImage(bitmap, 0, 0, largura, altura);
  bitmap.close?.();
  const dataUrl = canvas.toDataURL('image/jpeg', LOGO_QUALIDADE);
  return { base64: dataUrl.split(',')[1] ?? '', mime: 'image/jpeg' };
}

export function MarcaPrestador({ cliente, aoAtualizar, soLeitura = false }: {
  cliente: ClienteApp;
  aoAtualizar: (c: ClienteApp) => void;
  /** Modo ESPELHO ([DA-634]): a tela aparece inteira, e nao grava nada. ⚠️ Sem
   *  isto o campo salva sozinho (temporizador de 900ms) e o cliente levaria um
   *  403 vermelho por ter apenas ABERTO a tela de Configuracoes. */
  soLeitura?: boolean;
}): React.ReactElement {
  const [fantasia, setFantasia] = useState(cliente.fantasia ?? '');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);

  const assinatura = (cliente.fantasia ?? '').trim() || cliente.nome;

  /**
   * ⛔ A marca da loja nao assina papel de prestador (25/08/2026 — `marca-loja.ts`).
   *
   * ⚠️ **Isto aqui e' AVISO, nao trava.** Quem recusa e' o `PUT /perfil`; este campo
   * salva sozinho por `fetch`, entao uma checagem que so vivesse na tela seria
   * contornavel sem ferramenta nenhuma. O papel dela e' outro: sem o aviso, digitar
   * "FIO DE COBRE" faria o campo simplesmente **nao salvar** — e este formulario nao
   * tem botao Salvar, entao a pessoa sairia da tela achando que gravou.
   */
  const marcaDaLoja = !cliente.marca_loja_liberada && usaMarcaDaLoja(fantasia);

  async function salvarNome(): Promise<void> {
    // Guarda unica pros DOIS gatilhos (o `onBlur` e o temporizador de 900ms) —
    // repetir a condicao nos dois seria a mesma regra em dois lugares.
    if (ocupado || marcaDaLoja || soLeitura) return;
    setOcupado(true);
    setErro(null);
    try {
      const s = await appApi.salvarFantasia(fantasia.trim());
      aoAtualizar(s.cliente);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2500);
    } catch (e) {
      setErro((e as ErroApp).message);
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Salva sozinho, como o resto do aplicativo.
   *
   * ⚠️ **So o `onBlur` nao basta num celular.** Digitar o nome e fechar o app (ou
   * trocar de tela pelo gesto do sistema) pode nao produzir blur nenhum, e o campo
   * voltaria vazio sem nada avisar — que e' o mesmo motivo pelo qual o orcamento nao
   * tem botao Salvar ([DA-565]). O blur continua existindo: ele grava na hora quando
   * o dedo sai do campo, e este efeito cobre quem para de digitar e nao sai.
   */
  useEffect(() => {
    if (fantasia.trim() === (cliente.fantasia ?? '').trim()) return;
    const t = setTimeout(() => { void salvarNome(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fantasia]);

  async function escolherLogo(f: File | null): Promise<void> {
    if (!f || ocupado || soLeitura) return;
    setOcupado(true);
    setErro(null);
    try {
      const { base64, mime } = await reduzirLogo(f);
      const s = await appApi.enviarLogo(base64, mime);
      aoAtualizar(s.cliente);
    } catch (e) {
      setErro((e as ErroApp).message || 'Não consegui enviar a imagem.');
    } finally {
      setOcupado(false);
      if (arquivo.current) arquivo.current.value = '';
    }
  }

  async function tirarLogo(): Promise<void> {
    if (ocupado || soLeitura) return;
    setOcupado(true);
    setErro(null);
    try {
      const s = await appApi.removerLogo();
      aoAtualizar(s.cliente);
    } catch (e) {
      setErro((e as ErroApp).message);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 6px' }}>
        <Store size={20} style={{ color: COBRE, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16 }}>Minha empresa</div>
          {/* Diz o que esta valendo AGORA — campo vazio nao explica que o papel sai
              assinado com o nome da pessoa. */}
          <div style={{ fontSize: 13, color: CINZA, marginTop: 2 }}>
            Hoje o orçamento sai assinado como <b>{assinatura}</b>
          </div>
          {/* Controle travado DIZ por que (`CLAUDE.md` raiz, § UI): sem esta linha
              os botoes ficariam cinzas e mudos, e o beco sem saida seria meu. */}
          {soLeitura && (
            <div style={{ fontSize: 12.5, color: CINZA, marginTop: 4 }}>
              Modo espelho — só para ver, nada é gravado.
            </div>
          )}
        </div>
      </div>

      {/* ---- Imagem ---- */}
      <div style={{ padding: '10px 16px 4px' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>Imagem</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <div style={{
            width: 76, height: 76, flexShrink: 0, borderRadius: 10, overflow: 'hidden',
            border: `1px ${cliente.logo_url ? 'solid' : 'dashed'} #d6d3d1`, backgroundColor: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {cliente.logo_url
              ? <img src={cliente.logo_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <ImageIcon size={22} style={{ color: '#d6d3d1' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={() => arquivo.current?.click()}
              disabled={ocupado || soLeitura}
              style={{
                height: 42, borderRadius: 9, border: `1px solid ${COBRE}`, backgroundColor: '#fff',
                color: COBRE, fontSize: 14.5, fontWeight: 700, opacity: ocupado || soLeitura ? 0.6 : 1,
              }}
            >
              {cliente.logo_url ? 'Trocar imagem' : 'Escolher imagem'}
            </button>
            {cliente.logo_url && (
              <button
                type="button"
                onClick={() => void tirarLogo()}
                disabled={ocupado || soLeitura}
                style={{
                  height: 38, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff',
                  color: '#78716c', fontSize: 13.5, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Trash2 size={15} /> Tirar do papel
              </button>
            )}
          </div>
        </div>
        <input
          ref={arquivo}
          type="file"
          accept="image/*"
          onChange={(e) => void escolherLogo(e.target.files?.[0] ?? null)}
          style={{ display: 'none' }}
        />
      </div>

      {/* ---- Fantasia ---- */}
      <div style={{ padding: '14px 16px 16px' }}>
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>Fantasia</span>
          <input
            className="no-upper"
            value={fantasia}
            onChange={(e) => { setFantasia(e.target.value); setSalvo(false); }}
            onBlur={() => { if (fantasia.trim() !== (cliente.fantasia ?? '').trim()) void salvarNome(); }}
            readOnly={soLeitura}
            placeholder="Nome da sua empresa"
            maxLength={120}
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            style={{ ...estiloCampo, marginTop: 6 }}
          />
        </label>
        {/* O aviso da marca substitui a ajuda: dizer "vai no alto do orçamento"
            embaixo de um nome que nao vai ser gravado seria mentir na mesma linha. */}
        {marcaDaLoja ? (
          <div style={{
            marginTop: 8, padding: 10, borderRadius: 9,
            backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13, lineHeight: 1.45,
          }}>
            {RECUSA_MARCA_FANTASIA}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: CINZA, marginTop: 7, lineHeight: 1.5 }}>
            {salvo
              ? 'Salvo.'
              : 'Vai no alto do orçamento, no lugar do seu nome. Deixe em branco para assinar com seu próprio nome.'}
          </div>
        )}
        {erro && (
          <div style={{ marginTop: 9, padding: 10, borderRadius: 9, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
            {erro}
          </div>
        )}
      </div>
    </div>
  );
}
